/**
 * Planning table load data and load full wall session state.
 *
 * `startedAt` must match the same `performance.now()` origin as
 * `PLANNING_TABLE_RUM_DURATION_VITALS.loadTableWallTime` for the data load.
 * Each `startPlanningTableLoadWallTimeSession` replaces any previous session (supersedes in-flight loads).
 * If `lines_orders` stays empty, `report_loading` may never clear, so no full-UI vital is emitted.
 */

import { PLANNING_TABLE_RUM_DURATION_VITALS, recordPlanningTableRumDurationVital } from "./planningTableRumVitals";

import type { Store, Unsubscribe } from "redux";
import type { ReduxState } from "../reducers/reducers";

type WallTimeSession = {
    startedAt: number;
    orgUuid: string;
    lineGroupUuid: string;
    loadFullUiWallTimeEmitted: boolean;
};

type WallTimeSessionStart = Omit<WallTimeSession, "loadFullUiWallTimeEmitted">;

type FullUiWallTimeReadiness = {
    /** Table data is loaded (`!gantt_chart_lines_orders.loading`). */
    linesOrdersNotLoading: boolean;
    /** Initial quick-sim report is loaded (`report_data != null && !report_loading`). */
    mainReportReady: boolean;
};

let session: WallTimeSession | null = null;

/**
 * Starts a new planning table load wall time session.
 */
export function startPlanningTableLoadWallTimeSession(args: WallTimeSessionStart): void {
    session = {
        startedAt: args.startedAt,
        orgUuid: args.orgUuid,
        lineGroupUuid: args.lineGroupUuid,
        loadFullUiWallTimeEmitted: false,
    };
}

/**
 * Clears the active session (e.g. planning data failed before table path completed).
 */
export function cancelPlanningTableLoadWallTimeSession(): void {
    session = null;
}

/**
 * Emits `PLANNING_TABLE_RUM_DURATION_VITALS.loadFullUiWallTime` once per session when the table data is loaded
 * and the initial quick-sim report is ready.
 * Sets `loadFullUiWallTimeEmitted` synchronously before awaiting RUM so rapid Redux notifications cannot overlap emits.
 */
export async function tryEmitPlanningTableLoadFullUiWallTime(readiness: FullUiWallTimeReadiness): Promise<void> {
    if (!readiness.linesOrdersNotLoading || !readiness.mainReportReady) {
        return;
    }

    const activeSession = session;
    if (!activeSession || activeSession.loadFullUiWallTimeEmitted) {
        return;
    }

    activeSession.loadFullUiWallTimeEmitted = true;

    await recordPlanningTableRumDurationVital({
        name: PLANNING_TABLE_RUM_DURATION_VITALS.loadFullUiWallTime,
        durationMs: performance.now() - activeSession.startedAt,
        context: {
            orgUuid: activeSession.orgUuid,
            lineGroupUuid: activeSession.lineGroupUuid,
        },
    });
}

/**
 * Subscribes to the planning-table Redux store and emits `PLANNING_TABLE_RUM_DURATION_VITALS.loadFullUiWallTime`
 * when the table data is loaded and the initial quick-sim report is ready (`report_data` set and not `report_loading`).
 */
export function subscribePlanningTableLoadFullUiWallTime(store: Store<ReduxState>): Unsubscribe {
    return store.subscribe(() => {
        const state = store.getState();
        const linesOrdersNotLoading = !state.gantt_chart_lines_orders.loading;
        const report = state.gantt_chart_report;
        const mainReportReady = report.report_data !== null && !report.report_loading;

        void tryEmitPlanningTableLoadFullUiWallTime({
            linesOrdersNotLoading,
            mainReportReady,
        });
    });
}
